import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KindergartenBasicInfoDto } from '../../../common/interfaces/kindergarten.interface';
import { loadAdminKindergartens } from '../financeDirectory';

import { palette } from '../../../theme';
/** Above this many kindergartens the menu gets a filter; below it a filter is just in the way. */
const FILTER_THRESHOLD = 8;

/** "Mesec" → "ME", "Sunce test" → "ST": the mark when a kindergarten has no logo. */
export const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[1][0]}`).toUpperCase();
};

const Mark: React.FC<{ name?: string }> = ({ name }) => (
  <Box
    aria-hidden
    sx={{
      width: 32,
      height: 32,
      flexShrink: 0,
      borderRadius: 2,
      backgroundColor: palette.surface.tint,
      color: palette.primary.main,
      fontSize: 13,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {name ? initialsOf(name) : '?'}
  </Box>
);

/**
 * Which kindergarten a Finance page is about, on the right of its title.
 *
 * It replaces the breadcrumb on section pages: the sidebar already says where you are, and
 * the one thing the crumb trail was really used for was switching kindergarten. With a single
 * kindergarten there is nothing to switch to, so it is plain text, not a button that does
 * nothing.
 *
 * The kindergarten stays in `?kindergartenId=`; the page decides where a switch lands.
 */
const KindergartenSwitcher: React.FC<{
  kindergartenId: string;
  href: (kindergartenId: string) => string;
  /** Lets a page with unsaved edits ask before the switch; it calls `proceed` to go on. */
  guard?: (proceed: () => void) => void;
}> = ({ kindergartenId, href, guard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [kindergartens, setKindergartens] = useState<KindergartenBasicInfoDto[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadAdminKindergartens()
      .then(items => !cancelled && setKindergartens(items))
      // the page reports the failure; the switcher just has nothing to offer
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const current = kindergartens.find(item => item.id === kindergartenId);
  const shown = useMemo(
    () => kindergartens.filter(item => item.name.toLowerCase().includes(filter.trim().toLowerCase())),
    [kindergartens, filter]
  );
  const canSwitch = kindergartens.length > 1 || (!current && kindergartens.length > 0);

  const body = (
    <>
      <Mark name={current?.name} />
      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: palette.text.secondary, lineHeight: 1.3 }}>{t('kindergartenSwitcher.label')}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }} noWrap>
          {current?.name ?? (kindergartenId ? '…' : t('kindergartenSwitcher.pick'))}
        </Typography>
      </Box>
    </>
  );

  if (!canSwitch) {
    return (
      <Box sx={{ height: 44, display: 'inline-flex', alignItems: 'center', gap: 1.25, p: '0 12px 0 6px', maxWidth: 280 }}>{body}</Box>
    );
  }

  return (
    <>
      <ButtonBase
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label={t('kindergartenSwitcher.switch', { name: current?.name ?? t('kindergartenSwitcher.pick') })}
        onClick={event => {
          setFilter('');
          setAnchor(event.currentTarget);
        }}
        sx={{
          height: 44,
          gap: 1.25,
          p: '0 12px 0 6px',
          maxWidth: 280,
          border: `1px solid ${palette.field.border}`,
          borderRadius: '10px',
          backgroundColor: palette.surface.main,
          '&:hover': { backgroundColor: palette.surface.subtle }
        }}
      >
        {body}
        <ExpandMoreIcon sx={{ color: palette.text.secondary, ml: 0.5 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {kindergartens.length > FILTER_THRESHOLD && (
          <Box sx={{ px: 1.5, pb: 1 }} onKeyDown={event => event.stopPropagation()}>
            <TextField size="small" autoFocus fullWidth placeholder={t('breadcrumbs.filter')} value={filter} onChange={event => setFilter(event.target.value)} />
          </Box>
        )}
        {shown.map(item => (
          <MenuItem
            key={item.id}
            selected={item.id === kindergartenId}
            onClick={() => {
              setAnchor(null);
              if (item.id === kindergartenId) return;
              const go = () => navigate(href(item.id));
              if (guard) guard(go);
              else go();
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>{item.id === kindergartenId && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{item.name}</ListItemText>
          </MenuItem>
        ))}
        {!shown.length && (
          <MenuItem disabled>
            <ListItemText>{t('breadcrumbs.nothingMatches')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default KindergartenSwitcher;
